<script setup lang="js">
import axios from "axios";
import { defineProps, onMounted, ref, watch } from "vue";
import { Formatter } from "@/utilities/Formatter";

const props = defineProps({
  refresh: {
    type: Boolean,
    default: false
  }
});

const result = ref(null);
const loading = ref(false);
const error = ref(false);

const load = async () => {
  try {
    result.value = null;
    error.value = false;
    loading.value = true;
    const response = await axios.get(`${window.location.origin}/ui/v3/dashboard/remain`);
    result.value = response.data;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

onMounted(load);
watch(() => props.refresh, load);
</script>

<template>
  <article class="remains-card">
    <template v-if="result">
      <div class="remains-card__eyebrow">{{ $filters.localize("Dashboard.remains.eyebrow") }}</div>
      <h3 class="remains-card__title">{{ $filters.localize("Dashboard.remains.title") }}</h3>

      <div class="remains-card__count">{{ result.totalCount }} {{ $filters.localize("Dashboard.remains.pairs") }}</div>

      <div class="remains-card__stats">
        <div class="remains-card__stat">
          <div class="remains-card__stat-label">{{ $filters.localize("Dashboard.metrics.purchase") }}</div>
          <div class="remains-card__stat-value">{{ Formatter.toCurrency(result.totalPurchaseCost) }}</div>
        </div>

        <div class="remains-card__stat">
          <div class="remains-card__stat-label">{{ $filters.localize("OrderPage.kind.wholesale") }}</div>
          <div class="remains-card__stat-value">{{ Formatter.toCurrency(result.totalWholeSalePrice) }}</div>
        </div>

        <div class="remains-card__stat">
          <div class="remains-card__stat-label">{{ $filters.localize("OrderPage.kind.retail") }}</div>
          <div class="remains-card__stat-value">{{ Formatter.toCurrency(result.totalRetailPrice) }}</div>
        </div>
      </div>
    </template>

    <template v-else-if="error">
      <div class="dashboard-state dashboard-state--error">
        {{ $filters.localize("Dashboard.states.remainsError") }}
      </div>
    </template>

    <template v-else>
      <div class="dashboard-skeleton dashboard-skeleton--card">
        <div class="dashboard-skeleton__line dashboard-skeleton__line--title"></div>
        <div class="dashboard-skeleton__line dashboard-skeleton__line--meta"></div>
        <div class="dashboard-skeleton__stack">
          <div v-for="index in 3" :key="index" class="dashboard-skeleton__tile"></div>
        </div>
      </div>
    </template>

    <div v-if="loading && result" class="dashboard-loader-overlay">
      <div class="dashboard-loader"></div>
    </div>
  </article>
</template>

<style scoped>
.remains-card {
  position: relative;
  min-height: 300px;
  padding: 1.25rem;
  border-radius: 24px;
  border: 1px solid rgba(23, 50, 77, 0.08);
  background: #f3f6fa;
  box-shadow: 0 18px 40px rgba(23, 50, 77, 0.08);
  overflow: hidden;
}

.remains-card__eyebrow {
  color: #6f8194;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.35rem;
  font-weight: 700;
}

.remains-card__title {
  margin: 0;
  color: #17324d;
  font-size: 1.3rem;
}

.remains-card__count {
  margin-top: 0.9rem;
  color: #17324d;
  font-size: 1.1rem;
  font-weight: 700;
}

.remains-card__stats {
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
}

.remains-card__stat {
  padding: 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.remains-card__stat-label {
  margin-bottom: 0.35rem;
  color: #6f8194;
  font-size: 0.82rem;
}

.remains-card__stat-value {
  color: #17324d;
  font-weight: 700;
}

.dashboard-state {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  border-radius: 22px;
  text-align: center;
  font-weight: 600;
}

.dashboard-state--error {
  border: 1px dashed rgba(138, 91, 91, 0.24);
  background: rgba(255, 246, 246, 0.92);
  color: #8a5b5b;
}

.dashboard-skeleton--card {
  min-height: 240px;
}

.dashboard-skeleton__line,
.dashboard-skeleton__tile {
  background: linear-gradient(90deg, #edf4fb 0%, #f8fbfe 50%, #edf4fb 100%);
  background-size: 200% 100%;
  animation: dashboard-shimmer 1.5s infinite linear;
  border: 1px solid rgba(23, 50, 77, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.dashboard-skeleton__line {
  height: 14px;
  border-radius: 999px;
}

.dashboard-skeleton__line--title {
  width: 56%;
  height: 24px;
}

.dashboard-skeleton__line--meta {
  width: 40%;
  margin-top: 0.85rem;
}

.dashboard-skeleton__stack {
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
}

.dashboard-skeleton__tile {
  min-height: 72px;
  border-radius: 18px;
}

.dashboard-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(2px);
}

.dashboard-loader {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid rgba(23, 50, 77, 0.12);
  border-top-color: #17324d;
  animation: dashboard-spin 0.8s linear infinite;
}

@keyframes dashboard-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
